import { InformationCard, YesObject } from '@yes.technology/react-toolkit'
import { useCallback, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18n-lite'
import Iframe from 'shared/components/Iframe'
import { useIframe } from 'shared/hooks'
import {
  CANCEL_SELECT_AND_LINK_POST_MESSAGE_TYPE,
  SELECT_AND_LINK_POST_MESSAGE_TYPE
} from './SelectAndLink.constants'
import { SelectAndLinkSession } from './SelectAndLink.types'
import {
  CardContent,
  cardStyle,
  CardWrapper,
  FullScreenWrapper,
  IframeWrapper
} from './SelectAndLinkCard.styles'
import { buildSelectAndLinkIframeUrl } from './utils/buildSelectAndLinkIframeUrl'

type SelectAndLinkCardProps = {
  session: SelectAndLinkSession
  onClose: () => void
  onSelect: (values: YesObject[]) => void
  onCancel: () => void
}

function SelectAndLinkCard({
  session,
  onClose,
  onSelect,
  onCancel
}: SelectAndLinkCardProps) {
  const { t } = useTranslation()

  const iframeUrl = useMemo(
    () => buildSelectAndLinkIframeUrl(session),
    [session]
  )

  const { iframeAttributes } = useIframe({
    origin: iframeUrl,
    src: iframeUrl,
    returnOnFinishedInteraction: false
  })

  const onReceivedMessage = useCallback(
    (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return
      }

      const type = event.data?.type

      if (type === SELECT_AND_LINK_POST_MESSAGE_TYPE) {
        onSelect(event.data.values ?? [])
        onClose()
        return
      }

      if (type === CANCEL_SELECT_AND_LINK_POST_MESSAGE_TYPE) {
        onCancel()
        onClose()
        return
      }
    },
    [onClose, onSelect, onCancel]
  )

  useEffect(() => {
    window.addEventListener('message', onReceivedMessage)
    return () => window.removeEventListener('message', onReceivedMessage)
  }, [onReceivedMessage])

  useEffect(() => {
    const previousOverflow = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = previousOverflow
    }
  }, [])

  return (
    <FullScreenWrapper>
      <CardWrapper>
        <InformationCard
          title={`${t('select-and-link.action')} - ${session.objectClassTitle}`}
          isOpen
          close={onClose}
          style={cardStyle}
          hideHeader={false}
        >
          <CardContent>
            {iframeAttributes && (
              <IframeWrapper>
                <Iframe
                  key={iframeAttributes.src}
                  {...iframeAttributes}
                  height={undefined}
                  style={{
                    flex: 1,
                    minHeight: 0,
                    width: '100%',
                    height: '100%'
                  }}
                />
              </IframeWrapper>
            )}
          </CardContent>
        </InformationCard>
      </CardWrapper>
    </FullScreenWrapper>
  )
}

export default SelectAndLinkCard
